import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

type DialogEditState = 'EDIT' | 'LOADING' | 'ERROR';

interface DialogEditData {
  prop: 'pickUp' | 'dropOff' | 'driver' | 'horaInicioOperacion';
  value: any;
  data?: any;
}

export interface DialogEditSavePayload {
  prop: 'pickUp' | 'dropOff' | 'driver' | 'horaInicioOperacion';
  value: any;
}

interface DialogEditCopy {
  title: string;
  description: string;
  fieldLabel: string;
  fieldHint: string;
}

const DEFAULT_COPY: DialogEditCopy = {
  title: 'Editar campo',
  description: 'Actualiza el valor sin perder el contexto del servicio.',
  fieldLabel: 'Valor',
  fieldHint: 'Escribe el nuevo dato y guarda los cambios.'
};

@Component({
  selector: 'app-dialog-edit',
  templateUrl: './dialog-edit.component.html',
  styleUrls: ['./dialog-edit.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogEditComponent implements OnInit {
  @Output() saveRequested = new EventEmitter<DialogEditSavePayload>();

  valueProp: any = '';
  prop: 'pickUp' | 'dropOff' | 'driver' | 'horaInicioOperacion' = 'pickUp';
  state: DialogEditState = 'EDIT';
  errorMessage = '';
  dialogTitle = DEFAULT_COPY.title;
  dialogDescription = DEFAULT_COPY.description;
  fieldLabel = DEFAULT_COPY.fieldLabel;
  fieldHint = DEFAULT_COPY.fieldHint;
  contextText = '';
  isTimeField = false;

  private readonly copyByProp: Record<string, DialogEditCopy> = {
    horaInicioOperacion: {
      title: 'Editar hora de inicio',
      description: 'Define la hora exacta en la que comienza la operación.',
      fieldLabel: 'Hora de inicio',
      fieldHint: 'Usa formato de 24 horas. Ejemplo: 08:30.'
    },
    dropOff: {
      title: 'Editar Drop Off',
      description: 'Describe el punto de llegada o cualquier referencia útil.',
      fieldLabel: 'Detalles del Drop Off',
      fieldHint: 'Incluye ubicación, referencia o instrucciones de llegada.'
    },
    pickUp: {
      title: 'Editar Pick Up',
      description:
        'Completa el punto de recogida para que el equipo lo vea claro.',
      fieldLabel: 'Detalles del Pick Up',
      fieldHint: 'Incluye dirección, hotel o referencia principal.'
    },
    driver: {
      title: 'Editar Driver',
      description:
        'Registra el nombre del conductor y su contacto principal. Esta información está pensada principalmente para transporte de pasajeros.',
      fieldLabel: 'Información del conductor + contacto.',
      fieldHint: 'Agrega nombre, apellido y teléfono si aplica.'
    }
  };

  constructor(
    public dialogRef: MatDialogRef<DialogEditComponent>,
    private readonly cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: DialogEditData
  ) {}

  ngOnInit(): void {
    this.prop = this.data?.prop ?? 'pickUp';
    this.isTimeField = this.prop === 'horaInicioOperacion';

    const copy = this.copyByProp[this.prop] ?? DEFAULT_COPY;
    this.dialogTitle = copy.title;
    this.dialogDescription = copy.description;
    this.fieldLabel = copy.fieldLabel;
    this.fieldHint = copy.fieldHint;
    this.contextText = this.buildContextText(this.data?.data);
    this.valueProp = this.normalizeInitialValue(this.data?.value);
  }

  get normalizedValue(): any {
    if (typeof this.valueProp === 'string') {
      return this.valueProp.trim();
    }

    return this.valueProp;
  }

  get isSaveDisabled(): boolean {
    return (
      this.state === 'LOADING' ||
      this.normalizedValue === '' ||
      this.normalizedValue == null
    );
  }

  close(): void {
    if (this.state === 'LOADING') {
      return;
    }

    this.dialogRef.close({
      event: 'cancel'
    });
  }

  save(): void {
    const normalizedValue = this.normalizedValue;

    if (normalizedValue === '' || normalizedValue == null) {
      this.state = 'ERROR';
      this.errorMessage = 'Completa el campo antes de guardar.';
      return;
    }

    this.state = 'LOADING';
    this.errorMessage = '';
    this.cdr.markForCheck();
    this.saveRequested.emit({
      prop: this.prop,
      value: normalizedValue
    });
  }

  showError(message: string): void {
    this.state = 'ERROR';
    this.errorMessage = message;
    this.cdr.markForCheck();
  }

  onValueChange(): void {
    if (this.state === 'ERROR') {
      this.state = 'EDIT';
      this.errorMessage = '';
      this.cdr.markForCheck();
    }
  }

  private normalizeInitialValue(value: any): any {
    if (!this.isTimeField) {
      return value ?? '';
    }

    return this.normalizeTimeValue(value);
  }

  private normalizeTimeValue(value: any): string {
    if (value == null || value === '') {
      return '';
    }

    if (typeof value === 'string') {
      const timeMatch = value.match(/(\d{2}):(\d{2})(?::\d{2})?/);

      if (timeMatch) {
        return `${timeMatch[1]}:${timeMatch[2]}`;
      }
    }

    const parsedDate = new Date(value);
    if (!isNaN(parsedDate.getTime())) {
      return `${String(parsedDate.getHours()).padStart(2, '0')}:${String(
        parsedDate.getMinutes()
      ).padStart(2, '0')}`;
    }

    if (typeof value === 'string') {
      return value.slice(0, 5);
    }

    return '';
  }

  private buildContextText(data: any): string {
    const contextParts = [
      data?.fileNumber,
      data?.dayName,
      data?.day ? `Día ${data.day}` : null
    ].filter(Boolean);

    return contextParts.join(' · ');
  }
}
